import { ReactNode } from "react";
import {
  Sidebar,
  SidebarContent as SidebarContentComponent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { usePWABanner } from "@/hooks/usePWABanner";
import PWAInstallBanner from "@/components/mobile/PWAInstallBanner";
import { cn } from "@/lib/utils";
import {
  Folder,
  LogOut,
  LayoutDashboard,
  Settings,
  Users,
  BellRing,
  ChevronLeft,
  ChevronRight,
  Library,
  Bug,
  Database,
  HelpCircle,
  Component,
  Link2,
} from "lucide-react";
import { Roles } from "@/enums/roles.enum";
import { HelpCenter } from "./HelpCenter";

interface MainLayoutProps {
  children: ReactNode;
}

const CustomSidebarToggle = () => {
  const { toggleSidebar, state } = useSidebar();
  
  return (
    <Button
      onClick={toggleSidebar}
      variant="outline"
      size="icon"
      className="fixed top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full border bg-background shadow-md hover:bg-accent transition-all duration-200"
      style={{
        left: state === "expanded" ? "15rem" : "0.5rem",
      }}
    >
      {state === "expanded" ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </Button>
  );
};

const SidebarContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();

  const isActive = (path: string) => location.pathname === path;

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader>
        <div className="flex items-center space-x-2 px-4 py-2">
          <img src="/logo-flat.svg" alt="Quantum Warehouse Solutions logo" />
        </div>
      </SidebarHeader>
      <SidebarContentComponent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  className={isActive("/") ? "bg-primary/10 text-primary" : ""}
                  onClick={() => navigate("/")}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/projects") ? "bg-primary/10 text-primary" : ""
                  }
                  onClick={() => navigate("/projects")}
                >
                  <Folder className="mr-2 h-4 w-4" />
                  <span>Projects</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/notifications")
                      ? "bg-primary/10 text-primary"
                      : ""
                  }
                  onClick={() => navigate("/notifications")}
                >
                  <BellRing className="mr-2 h-4 w-4" />
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">
                      {unreadCount}
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/ahj-database") ? "bg-primary/10 text-primary" : ""
                  }
                  onClick={() => navigate("/ahj-database")}
                >
                  <Database className="mr-2 h-4 w-4" />
                  <span>AHJ Database</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {user?.role === Roles.ADMIN && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={
                        isActive("/users") ? "bg-primary/10 text-primary" : ""
                      }
                      onClick={() => navigate("/users")}
                    >
                      <Users className="mr-2 h-4 w-4" />
                      <span>Users</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
{/* 
        <SidebarGroup>
          <SidebarGroupLabel>Studio</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/studio/part-builder")
                      ? "bg-primary/10 text-primary"
                      : ""
                  }
                  onClick={() => navigate("/studio/part-builder")}
                >
                  <Component className="mr-2 h-4 w-4" />
                  <span>Part Builder</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/studio/connection-builder")
                      ? "bg-primary/10 text-primary"
                      : ""
                  }
                  onClick={() => navigate("/studio/connection-builder")}
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  <span>Connection Builder</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup> */}

        <div className="flex flex-col px-4 py-4 mt-auto">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                className={
                  isActive("/settings") ? "bg-primary/10 text-primary" : ""
                }
                onClick={() => navigate("/settings")}
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {user?.role === Roles.ADMIN && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  className={
                    isActive("/resources") ? "bg-primary/10 text-primary" : ""
                  }
                  onClick={() => navigate("/resources")}
                >
                  <Library className="mr-2 h-4 w-4" />
                  <span>Company Resource Library</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            <SidebarMenuItem>
              <HelpCenter trigger={
                <SidebarMenuButton>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  <span>Help & Resources</span>
                </SidebarMenuButton>
              } />
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                className={
                  isActive("/report-bug") ? "bg-primary/10 text-primary" : ""
                }
                onClick={() => navigate("/report-bug")}
              >
                <Bug className="mr-2 h-4 w-4" />
                <span>Report a Bug</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          
          <div className="flex flex-col space-y-3 mt-4">
            <div className="flex items-center space-x-3">
              <div className="bg-primary rounded-full w-8 h-8 flex items-center justify-center text-primary-foreground font-medium text-sm flex-shrink-0">
                {user.firstName?.charAt(0).toUpperCase()}{user.lastName?.charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium text-sm truncate">{`${user?.firstName} ${user?.lastName}`.trim()}</span>
                <span className="text-xs text-muted-foreground capitalize truncate">
                  {user?.role}
                </span>
              </div>
            </div>  
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => logout()}
            >
              <LogOut className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">Sign Out</span>
            </Button>
          </div>
        </div>
      </SidebarContentComponent>
    </Sidebar>
  );
};

const MainLayout = ({ children }: MainLayoutProps) => {
  const { isBannerVisible } = usePWABanner();
  
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        {/* PWA Install Banner */}
        <PWAInstallBanner />
        
        <SidebarContent />
        <CustomSidebarToggle />
        <main className={cn(
          "flex-1 p-6 overflow-y-auto w-full",
          isBannerVisible ? "pt-16" : "pt-6"
        )}>
          <div className="w-full max-w-none">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default MainLayout;
